import React, { useEffect, useState } from "react";
import { Save, KeyRound } from "lucide-react";
import { api, fmtErr } from "../../lib/api";
import { useSite } from "../../context/AppContext";
import { toast } from "sonner";

const inputCls = "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-800 focus:border-amber-500 bg-white";

export default function AdminSettings() {
  const { site, refreshSite } = useSite();
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
  const [pwBusy, setPwBusy] = useState(false);

  useEffect(() => {
    if (site?.settings) setS({ ...site.settings });
  }, [site]);

  if (!s) return <div className="py-16 flex justify-center"><div className="w-8 h-8 border-2 border-amber-500/30 border-t-amber-600 rounded-full animate-spin" /></div>;

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/site", { settings: s });
      await refreshSite();
      toast.success("Settings saved");
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setSaving(false);
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (pw.new_password.length < 8) { toast.error("New password must be at least 8 characters"); return; }
    if (pw.new_password !== pw.confirm) { toast.error("New passwords do not match"); return; }
    setPwBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: pw.current_password, new_password: pw.new_password });
      toast.success("Password updated");
      setPw({ current_password: "", new_password: "", confirm: "" });
    } catch (err) {
      toast.error(fmtErr(err, "Could not update password"));
    } finally {
      setPwBusy(false);
    }
  };

  return (
    <div data-testid="admin-settings">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-heading text-3xl font-semibold text-[#0B1D3A]">Website Settings</h2>
          <p className="text-slate-500 text-sm">Site-wide wording, privacy notice, and your password.</p>
        </div>
        <button onClick={save} disabled={saving} data-testid="admin-settings-save" className="inline-flex items-center gap-1.5 px-5 py-2 rounded-lg bg-[#0B1D3A] text-amber-300 text-sm font-semibold disabled:opacity-60">
          <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save Settings"}
        </button>
      </div>

      <div className="grid lg:grid-cols-2 gap-6 max-w-5xl">
        <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-4">
          <label className="block">
            <span className="block text-xs font-semibold text-slate-600 mb-1">Website title (browser tab)</span>
            <input value={s.siteTitle} onChange={(e) => setS({ ...s, siteTitle: e.target.value })} className={inputCls} data-testid="settings-site-title" />
          </label>
          <label className="block">
            <span className="block text-xs font-semibold text-slate-600 mb-1">Notification email (where new inquiries are sent)</span>
            <input type="email" value={s.ownerEmail} onChange={(e) => setS({ ...s, ownerEmail: e.target.value })} className={inputCls} data-testid="settings-owner-email" />
            <span className="block text-[11px] text-slate-400 mt-1">Each contact-form message is emailed here. Make sure it's an inbox you check.</span>
          </label>
          <label className="block">
            <span className="block text-xs font-semibold text-slate-600 mb-1">Consultation fee notice</span>
            <input value={s.consultationFee} onChange={(e) => setS({ ...s, consultationFee: e.target.value })} className={inputCls} data-testid="settings-fee-text" />
            <span className="block text-[11px] text-slate-400 mt-1">Shown near the trip-planning button and in the footer on every page.</span>
          </label>
          <label className="block">
            <span className="block text-xs font-semibold text-slate-600 mb-1">Privacy notice</span>
            <textarea rows={7} value={s.privacyNotice} onChange={(e) => setS({ ...s, privacyNotice: e.target.value })} className={inputCls} data-testid="settings-privacy-text" />
            <span className="block text-[11px] text-slate-400 mt-1">Shown on the /privacy page, linked in the footer.</span>
          </label>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-6">
          <h3 className="font-semibold text-[#0B1D3A] mb-1 flex items-center gap-2"><KeyRound className="w-4 h-4 text-amber-600" /> Change your password</h3>
          <p className="text-xs text-slate-400 mb-4">Use a long, unique password you don't use anywhere else.</p>
          <form onSubmit={changePassword} className="space-y-3" data-testid="change-password-form">
            <label className="block">
              <span className="block text-xs font-semibold text-slate-600 mb-1">Current password</span>
              <input type="password" autoComplete="current-password" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} className={inputCls} data-testid="settings-current-password" required />
            </label>
            <label className="block">
              <span className="block text-xs font-semibold text-slate-600 mb-1">New password (min 8 characters)</span>
              <input type="password" autoComplete="new-password" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} className={inputCls} data-testid="settings-new-password" required />
            </label>
            <label className="block">
              <span className="block text-xs font-semibold text-slate-600 mb-1">Confirm new password</span>
              <input type="password" autoComplete="new-password" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} className={inputCls} data-testid="settings-confirm-password" required />
            </label>
            <button type="submit" disabled={pwBusy} data-testid="settings-change-password-button" className="btn-brand !rounded-lg !py-2.5 disabled:opacity-60">
              {pwBusy ? "Updating…" : "Update Password"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
